<template>
  <div class="product-editor">
    <ui-loader
      v-if="loading"
      class="product-editor-loader"
      size="64"
    />

    <template v-if="!contentIsEmpty">
      <product-markers
        class="markers"
        :markers="product.markers"
        :plates="product.plates"
      />

      <h1 class="title">
        {{ name }}
        <vue-client-only>
          <span v-if="code && !adaptive.isMobile">
            {{ code }}
          </span>
        </vue-client-only>
      </h1>

      <div class="gallery">
        <swiper
          :items="images"
          v-slot="{ item }"
          :slides-to-show="1"
          dots
          arrows
        >
          <media :media="item" width="400" :alt="name" />
        </swiper>

        <vue-client-only>
          <favorite-button
            v-if="product.id && !adaptive.isMobile"
            :product-id="product.id"
            :product-price="currentPrice"
            :product-code="product.code"
            :quantity="computedQty"
            :sku-id="activeSku.id"
          />
        </vue-client-only>
      </div>

      <div class="prices">
        <div :class="['price price-rub', {discount: isDiscount}]">
          {{ currentPrice | price }}
        </div>
        <div
          v-if="isDiscount"
          class="old-price"
        >
          {{ product.price.basePrice | price }}
        </div>
      </div>

      <vue-client-only>
        <favorite-button
          v-if="product.id && adaptive.isMobile"
          class="favourite"
          :product-id="product.id"
          :product-code="product.code"
          :sku-id="activeSku.id"
        />
      </vue-client-only>

      <select-color
        class="colors"
        label="Цвета"
        :colors="colors"
        :active-color="product.id"
        @change="loadProduct"
      />

      <select-size
        visible-hint
        class="sizes"
        label="Размеры"
        :sizes="sizes"
        :active-sku="activeSku"
        @change="changeSize"
      >
        <sizes-table
          class="tables"
          v-if="product.sizeTable"
          :product-id="product.id"
        />
      </select-size>

      <product-bonus :bonus="product.bonus" />

      <div class="buttons">
        <ui-button
          wide
          :disabled="isSubmitDisabled"
          text="Сохранить изменения"
          @click="submit"
        />

        <ui-select
          :value="computedQty"
          placeholder="Выберите количество"
          :options="qtyOptions"
          @change="changeQty"
        />
      </div>

      <ui-notification
        class="sold-out"
        v-if="!product.available"
        title="Товар распродан"
      />
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import VueClientOnly from 'vue-client-only';

import ProductMarkers from './product-markers';
import FavoriteButton from '../favorite-button/favorite-button';
import SelectColor from './select-color';
import SelectSize from './select-size';
import SizesTable from './size-table';
import ProductBonus from './product-bonus';

export default {
  name: 'product-editor',


  components: {
    ProductBonus,
    SizesTable,
    SelectSize,
    SelectColor,
    FavoriteButton,
    ProductMarkers,
    VueClientOnly,
  },
  
  inject: ['adaptive'],

  props: {
    params: { // исх. параметры редактируемого продукта
      type: Object,
      required: true
    }
  },

  data: () => ({
    qtyOptions: new Array(10).fill(null).map((v, i) => ({ name: i + 1 })),
    size: '',
    quantity: '',
    product: {},
    colors: [],
    loading: false,
    contentIsEmpty: true, // убирает дёрганье верстки при первой загрузке без контента
  }),

  computed: {
    sizes() {
      return this.product.skus || [];
    },

    computedSize() { // завязано на названии размера, а не на skuId
        return this.size || this.params.size;
    },

    activeSku() {
      return this.computedSize
          ? this.product?.skus?.find(sku => sku.available && sku.size === this.computedSize) || {}
          : {};
    },

    computedQty() {
      return this.quantity || this.params.quantity;
    },

    images() {
      return (this.product.media)?.filter(({ type }) => type === 'photo') || [];
    },

    name() {
      return this.product.name || '';
    },

    code() {
      return this.product.code || '';
    },

    currentPrice() {
      return this.product.price?.discountPrice || this.product.price?.basePrice;
    },

    isDiscount() {
      return Number(this.product.price?.discountRate);
    },

    changeProductNeeded() { // если размер или цвет отличаются от исходных
      return (this.size && this.size !== this.params.size) || this.product.id !== this.params.id;
    },

    isSubmitDisabled() {
      return (!this.changeProductNeeded && (this.params.quantity === this.computedQty)) // если изм. исх. данных
              || !Object.keys(this.activeSku).length // если не выбран размер
    },
  },

  created() {
    if (!this.$isServer) {
      this.init(this.params.id);
    }
  },

  methods: {
    ...mapActions({
      CHANGE_QTY: 'cart/changeQty',
      CHANGE_PRODUCT: 'cart/changeProduct',
    }),

    async init(id) {
      if (id === this.product?.id) return;

      this.product = {};
      await this.loadProduct(id);
      this.setColors();
    },

    async loadProduct(productId) {
      this.loading = true;

      try {
        await this.$store.$api.loadProduct({ productId }).then(product => this.product = product)
      } catch (e) {
        this.throwError('Не удалось получить информацию о товаре. Попробуйте позже.');
      } finally {
        this.loading = false;
        if (this.contentIsEmpty) this.contentIsEmpty = false;
      }
    },

    async submit() {
      this.loading = true;

      let productParams = {
        lineIds: this.params?.lineIds,
        productId: this.product.id,
        skuId: this.activeSku.id,
        shippingGroupId: this.params?.shippingGroupId,
      };

      try {
        if (this.changeProductNeeded) {
          await this.CHANGE_PRODUCT({
            ...productParams,
            quantity: this.quantity || this.params.quantity,
            storeId: this.params.storeId,
          })
        } else {
          await this.CHANGE_QTY({
            ...productParams,
            quantity: this.params.quantity,
            updatedQuantity: this.quantity,
            shippingGroupId: this.params?.shippingGroupId
          })
        }
        this.closeEditor();
      } catch(e) {
        this.throwError('Не удалось сохранить изменения. Попробуйте позже.');
      }
    },

    closeEditor() {
      this.loading = false;
      this.$emit('on-close');
    },

    throwError(message = '') {
      this.loading = false;
      this.$store.dispatch('notifications/create', {
        title: 'Ошибка',
        message: message,
      });
    },

    changeSize({size}) {
      this.size = size;
    },

    changeQty(qty) {
      this.quantity = qty.name;
    },

    setColors() {
      // чтобы не сбивался порядок
      this.colors = this.product.models?.map(m => ({
        modelId: m.id,
        ...m.color
      })) || [];
    },
  },
};
</script>

<style lang="scss">
.product-editor {
  display: grid;
  /* stylelint-disable-next-line */
  grid-template-areas:
  'markers markers'
  'title title'
  'gallery gallery'
  'prices favourite'
  'colors colors'
  'sizes sizes'
  'tables tables'
  'bonus bonus'
  'buttons buttons';

  &-loader {
    z-index: 1;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: $blue;
    ~ * {
      opacity: .6;
      pointer-events: none;
    }
  }
  .markers {
    grid-area: markers;
  }
  .title {
    grid-area: title;
    margin-top: 8px;
    font-size: 20px;
    line-height: 28px;
    font-family: "VAGRounded", Arial, Helvetica, sans-serif;
    font-weight: 300;
    letter-spacing: -.2px;
  }
  .gallery {
    position: relative;
    grid-area: gallery;
    width: calc(100vw - 32px);
    margin-top: 12px;
    .product-markers {
      position: absolute;
      top: 19px;
      left: 20px;
      right: 30%;
    }
  }
  .swiper {
    height: 448px;
    &-wrapper {
      height: 400px;
      margin: 0;
    }
    .dots {
      position: absolute;
      bottom: 10px;
      width: 100%;
    }
    .arrows {
      left: 12px;
      right: 12px;
      top: 46%;
    }
    &-slides,
    &-slide {
      padding: 0;
    }
  }

  .prices {
    grid-area: prices;
    display: flex;
    align-items: center;
    font-size: 40px;
    line-height: 48px;

    .price {
      display: flex;
      font-weight: 600;
      font-size: 32px;
      line-height: 40px;

      &.discount {
        color: $red;
      }
    }

    .old-price {
      margin-left: 12px;
      color: $gray;
      font-weight: normal;
      font-size: 20px;
      line-height: 24px;
      text-decoration: line-through;
      transform: translateY(6px);
    }
  }

  .favorite-button {
    grid-area: favourite;
    width: 25px;
    height: 25px;
    margin-top: 4px;
    margin-left: auto;
  }

  .select-color,
  .select-size {
    margin-top: 4px;
  }

  .product-bonus {
    margin-bottom: 20px;
  }

  .select-color {
    grid-area: colors;

    li {
      padding: 4px;

      &.active {
        box-shadow: inset 0 0 0 1px $black;
      }
    }
  }

  .select-size {
    grid-area: sizes;

    &-label {
      margin: 12px 0 0;
    }

    &-sizes {
      padding: 0;

      li {
        margin-top: 4px;
        padding: 6px 8px;
        min-width: 32px;
        font-size: 14px;
        line-height: 20px;
      }
    }

    &-hint {
      margin-bottom: 20px;
    }
  }

  .size-table {
    grid-area: tables;
  }

  .product-bonus {
    grid-area: bonus;
    font-size: 14px;
  }

  .buttons {
    display: flex;
    grid-area: buttons;
    margin-top: 4px;

    & > .ui-button {
      width: calc(100% - 64px);
      margin-right: 8px;
    }

    & > .ui-select {
      width: 60px;
    }
  }

  .select-color-label,
  .select-size-label {
    font-size: 12px;
    line-height: 16px;
    margin: 12px 0 0;
    color: $gray;
  }
}

@include breakpoint("tablet") {
  .product-editor {
    /* stylelint-disable-next-line */
    grid-template-areas:
    'gallery markers'
    'gallery title'
    'gallery prices'
    'gallery colors'
    'gallery sizes'
    'gallery tables'
    'gallery buttons'
    'gallery bonus';
    align-items: flex-start;
    grid-column-gap: 24px;
    grid-template-columns: 384px auto;
    grid-template-rows: minmax(4px, auto) minmax(36px, auto) 38px minmax(68px, auto) minmax(74px, auto) 0 58px 1fr;
    height: 100%;
    padding-right: 25px;

    .markers {
      margin-top: 10px;
    }

    .title {
      margin-top: 13px;

      & > span {
        display: block;
        font-size: 14px;
        line-height: 20px;
        color: $gray_l;
      }
    }

    .gallery {
      width: auto;
      height: 100%;
      margin-top: 0;

      & > .favorite-button {
        position: absolute;
        top: 29px;
        right: 24px;
        margin-top: 0;
      }
    }

    .swiper {
      width: auto;
      height: 100%;

      &-wrapper {
        height: 100%;
      }
    }

    .prices {
      margin-top: 5px;
    }

    .select-color-label,
    .select-size-label {
      font-size: 14px;
    }

    .select-size {
      margin-top: 4px;

      &-sizes {
        li {
          margin-top: 8px;
        }
      }
    }

    .size-table {
      margin-top: 0;

      & > span {
        font-size: 14px;
        line-height: 16px;
      }
    }

    .buttons {
      & > .ui-button,
      .ui-select-input {
        height: 56px;
      }

      & > .ui-button {
        font-size: 16px;
        line-height: 24px;
        font-weight: normal;
      }

      .ui-select-input {
        input {
          height: 100%;
        }

        svg {
          top: 20px;
        }
      }
    }

    .product-bonus {
      margin-top: 16px;
    }
  }
}

@include breakpoint("desktop") {
  .product-editor {
    grid-template-columns: 439px auto;

    .markers {
      margin-top: 5px;
    }

    .title {
      font-size: 24px;
      line-height: 32px;
      margin-top: 10px;
    }

    .gallery {
      & > .favorite-button {
        top: 12px;
        right: 10px;
        width: 40px;
        height: 36px;
      }
    }

    .buttons {
      & > .ui-select {
        width: 76px;
      }
    }
  }
}
</style>
